import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { carReportsApi, CreateReportDto, CarReport as ApiCarReport, ReportsListResponse } from '../api/carReportsApi';

export type CarReport = ApiCarReport;

export const useCarTechProfile = (carId: string) => {
  return useQuery({
    queryKey: ['car-tech-profile', carId],
    queryFn: () => carReportsApi.getTechProfile(carId),
  });
};

export const useCreateCarReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateReportDto) => {
      return carReportsApi.createReport(data);
    },
    onSuccess: () => {
      // Invalidate the reports query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
    onError: (error) => {
      console.error('Failed to create report:', error);
      throw error; // Re-throw to be handled by the component
    }
  });
};

export const useRentingReports = (rentingId: string) => {
  return useQuery<ReportsListResponse['data']>({
    queryKey: ['reports', rentingId],
    queryFn: async () => {
      const response = await carReportsApi.getReports();
      return response.data.filter(report => report.renting.id === rentingId);
    },
  });
};

export const useUpdateCarReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateReportDto> }) =>
      carReportsApi.updateReport(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['report', id] });
    },
  });
};

export const useReport = (id: string) => {
  return useQuery({
    queryKey: ['report', id],
    queryFn: () => carReportsApi.getReport(id),
  });
};

export const useAllReports = () => {
  return useQuery<ReportsListResponse>({
    queryKey: ['reports'],
    queryFn: () => carReportsApi.getReports(),
  });
};
